import { toaster } from "@/components/ui/toaster";
import { useMerchant } from "@/hooks/useMerchant";
import useNumberFormatter from "@/hooks/useNumberFormatter";
import { usePermissions } from "@/hooks/usePermissions";
import {
  Button,
  CloseButton,
  Dialog,
  Heading,
  Portal,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import { formatDocument } from "../../screenings/columns";
import DetailList, { Detail } from "../../screenings/components/DetailList";
import DetailSection from "../../screenings/components/DetailSection";
import Participants from "../../screenings/components/Participants";
import {
  propertyTypeLabels,
  propertyUseLabels,
} from "../../screenings/mappers/screening.mapper";
import { Screening } from "../../screenings/types";
import { useFetchScreening } from "../../screenings/useFetchScreening";
import { useProtectTRPC } from "../../trpc";
import { canDownloadCertificate, retirementReasonLabels } from "../changes";
import { formatCalendarDate, formatMonth } from "../columns";
import { Contract } from "../types";
import { useDownloadCertificate } from "../useDownloadCertificate";
import { useInvalidateContracts } from "../useInvalidateContracts";
import StatusBadge from "./StatusBadge";

type Props = {
  open: boolean;
  onClose: () => void;
  contract?: Contract;
};

type FormatCurrency = (value: number) => string;

const formatPenalty = (
  contract: Contract,
  penaltyMonths: number,
  formatCurrency: FormatCurrency
) =>
  `${penaltyMonths} ${
    penaltyMonths === 1 ? "canon" : "cánones"
  } (${formatCurrency(contract.monthlyRent * penaltyMonths)})`;

const buildContractDetails = (contract: Contract): Detail[] => [
  { label: "Número de contrato", value: contract.contractNumber },
  { label: "Estado", value: <StatusBadge status={contract.status} /> },
  {
    label: "Inicio del arrendamiento",
    value: formatCalendarDate(contract.rentalsContractStartDate),
  },
  {
    label: "Fin del arrendamiento",
    value: formatCalendarDate(contract.rentalsContractEndDate),
  },
  {
    label: "Ingreso a la fianza",
    value: formatCalendarDate(contract.enrolledAt),
  },
];

const buildChangeDetails = (
  contract: Contract,
  formatCurrency: FormatCurrency
): Detail[] => {
  const { renewal, retirement } = contract.pendingChanges ?? {};

  return [
    ...(renewal
      ? [
          {
            label: "Renovación programada",
            value: `Desde ${formatMonth(
              renewal.effectiveMonth
            )}: canon ${formatCurrency(
              renewal.monthlyRent
            )}, fin ${formatCalendarDate(renewal.rentalsContractEndDate)}`,
          },
        ]
      : []),
    ...(retirement
      ? [
          {
            label: "Retiro en trámite",
            value: `Sale de la fianza en ${formatMonth(
              retirement.effectiveMonth
            )} · ${retirementReasonLabels[retirement.reason]}`,
          },
        ]
      : []),
    ...(contract.retiredAt
      ? [
          {
            label: "Fuera de la fianza",
            value: `Desde el ${formatCalendarDate(contract.retiredAt)}${
              contract.retirementReason
                ? ` · ${retirementReasonLabels[contract.retirementReason]}`
                : ""
            }`,
          },
        ]
      : []),
  ];
};

const buildCoverageDetails = (
  contract: Contract,
  formatCurrency: FormatCurrency
): Detail[] => [
  {
    label: "Canon de arrendamiento",
    value: `Hasta ${contract.coverages.months} meses`,
  },
  ...(contract.coverages.integral
    ? [
        {
          label: "Fianza integral",
          value: formatCurrency(contract.coverages.integral),
        },
      ]
    : []),
  ...(contract.coverages.penalty
    ? [
        {
          label: "Cláusula penal",
          value: formatPenalty(
            contract,
            contract.coverages.penalty,
            formatCurrency
          ),
        },
      ]
    : []),
];

const buildPropertyDetails = (
  contract: Contract,
  formatCurrency: FormatCurrency,
  screening?: Screening
): Detail[] => [
  { label: "Dirección", value: contract.propertyAddress || "-" },
  ...(screening?.propertyCity
    ? [{ label: "Ciudad", value: screening.propertyCity }]
    : []),
  { label: "Uso", value: propertyUseLabels[contract.propertyUse] },
  ...(screening?.propertyType
    ? [
        {
          label: "Tipo de inmueble",
          value: propertyTypeLabels[screening.propertyType],
        },
      ]
    : []),
  { label: "Canon mensual", value: formatCurrency(contract.monthlyRent) },
  ...(contract.adminFee !== undefined
    ? [{ label: "Administración", value: formatCurrency(contract.adminFee) }]
    : []),
  ...(screening?.vatFee
    ? [{ label: "IVA", value: formatCurrency(screening.vatFee) }]
    : []),
];

const ParticipantsContent = ({
  screening,
  isLoading,
  isError,
}: {
  screening?: Screening;
  isLoading: boolean;
  isError: boolean;
}): JSX.Element => {
  if (isError)
    return (
      <Text fontSize="sm" color="gray.500">
        No fue posible cargar los participantes de este contrato.
      </Text>
    );

  if (isLoading || !screening) return <Spinner size="sm" />;

  return <Participants screening={screening} showAuthorization={false} />;
};

const ContractDetailModal = ({
  open,
  onClose,
  contract,
}: Props): JSX.Element => {
  const trpc = useProtectTRPC();
  const { merchant } = useMerchant();
  const { isEditor } = usePermissions();
  const invalidateContracts = useInvalidateContracts();
  const formatCurrency = useNumberFormatter("es-CO", "COP", "currency");
  const { downloadCertificate, downloadingIds } = useDownloadCertificate();
  const { screening, isLoading, isError } = useFetchScreening(
    contract?.screeningId
  );

  const { mutate: cancelRetirement, isPending: isCanceling } = useMutation({
    ...trpc.contracts.cancelChange.mutationOptions(),
    onSuccess: () => {
      invalidateContracts();
      toaster.create({
        title: "Retiro cancelado",
        description: "El contrato sigue en la fianza.",
        type: "success",
        duration: 6000,
        meta: { closable: true },
      });
      onClose();
    },
    onError: () =>
      toaster.create({
        title: "Error",
        description:
          "No pudimos cancelar el retiro. Si ya pasó el corte de su mes, el retiro es definitivo.",
        type: "error",
        duration: 6000,
        meta: { closable: true },
      }),
  });

  return (
    <Dialog.Root
      lazyMount
      open={open}
      onOpenChange={(e) => !e.open && onClose()}
      size="md"
      scrollBehavior="inside"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            {contract && (
              <>
                <Dialog.Header borderBottom="1px solid #ebecef80" padding="4">
                  <Dialog.Title>
                    <Heading>{contract.tenantName}</Heading>
                    <Text textStyle="sm" color="gray.500" fontWeight="normal">
                      {formatDocument(contract)} · Contrato{" "}
                      {contract.contractNumber}
                    </Text>
                  </Dialog.Title>
                </Dialog.Header>
                <Dialog.CloseTrigger asChild>
                  <CloseButton marginTop="1" />
                </Dialog.CloseTrigger>
                <Dialog.Body py={6}>
                  <VStack gap={6} align="stretch">
                    <DetailSection title="Contrato">
                      <DetailList
                        details={[
                          ...buildContractDetails(contract),
                          ...buildChangeDetails(contract, formatCurrency),
                        ]}
                      />
                    </DetailSection>
                    <DetailSection title="Coberturas">
                      <DetailList
                        details={buildCoverageDetails(contract, formatCurrency)}
                      />
                    </DetailSection>
                    <DetailSection title="Inmueble">
                      <DetailList
                        details={buildPropertyDetails(
                          contract,
                          formatCurrency,
                          screening
                        )}
                      />
                    </DetailSection>
                    <DetailSection title="Participantes">
                      <ParticipantsContent
                        screening={screening}
                        isLoading={isLoading}
                        isError={isError}
                      />
                    </DetailSection>
                  </VStack>
                </Dialog.Body>
                <Dialog.Footer borderTop="1px solid #ebecef80">
                  <Button variant="outline" onClick={onClose}>
                    Cerrar
                  </Button>
                  {isEditor &&
                    merchant &&
                    contract.pendingChanges?.retirement && (
                      <Button
                        variant="outline"
                        colorPalette="red"
                        loading={isCanceling}
                        onClick={() =>
                          cancelRetirement({
                            rentalsMerchantId: merchant.id,
                            contractId: contract.id,
                          })
                        }
                      >
                        Cancelar retiro
                      </Button>
                    )}
                  {canDownloadCertificate(contract) && (
                    <Button
                      onClick={() => downloadCertificate(contract.id)}
                      loading={downloadingIds.has(contract.id)}
                    >
                      Descargar certificado
                    </Button>
                  )}
                </Dialog.Footer>
              </>
            )}
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default ContractDetailModal;
